import React from "react";
import { Card } from "./shared";
import * as m from "../lib/model";

const fmtM = (v) => m.fmt$(v || 0);
const fmtX = (v) => `${(Math.round((v || 0) * 10) / 10).toFixed(1)}x`;

const Num = ({ label, value, onChange, suffix, testid }) => {
  const money = suffix === "$";
  const display = money && value !== "" && value != null && !isNaN(Number(String(value).replace(/,/g, "")))
    ? (Number(String(value).replace(/,/g, "")) ? Number(String(value).replace(/,/g, "")).toLocaleString("en-US") : "")
    : (value ?? "");
  return (
    <label className="econ-ctl">
      <span>{label}</span>
      <div className={`econ-inp ${money ? "money" : ""}`}>
        {money && <em className="pre">$</em>}
        <input
          className="inp mono" type="text" inputMode="decimal" value={display} placeholder="0"
          onFocus={(e) => e.target.select()}
          onChange={(e) => {
            let clean = e.target.value.replace(/[^0-9.]/g, "");
            clean = clean.replace(/^0+(?=\d)/, "");
            const dot = clean.indexOf(".");
            if (dot !== -1) clean = clean.slice(0, dot + 1) + clean.slice(dot + 1).replace(/\./g, "");
            onChange(clean);
          }}
          data-testid={testid}
        />
        {!money && suffix && <em>{suffix}</em>}
      </div>
    </label>
  );
};

// QSBS tax engine + absolute vs relative leverage — driven by THIS deal's live LP outcome (`live`), not example numbers.
export default function RollUpEconomics({ B, onChange, live, lev, readOnly = false }) {
  const set = (k, v) => onChange({ ...B, [k]: v });
  const equity = live?.equity || 0, toInvestors = live?.toInvestors || 0, exitYear = live?.exitYear || 5;

  // QSBS (Section 1202) — exclusion tier by hold, capped at greater of $15M or 10× basis
  const rate = 0.238;
  const gain = Math.max(0, toInvestors - equity);
  const exclPct = exitYear >= 5 ? 1 : exitYear === 4 ? 0.75 : exitYear === 3 ? 0.5 : 0;
  const cap = Math.max(15000000, 10 * equity);
  const excludable = Math.min(gain, cap) * exclPct;
  const taxWithout = gain * rate;
  const taxWith = Math.max(0, gain - excludable) * rate;
  const netWithout = toInvestors - taxWithout;
  const netWith = toInvestors - taxWith;
  const multWithout = equity ? netWithout / equity : 0;
  const multWith = equity ? netWith / equity : 0;
  const tiers = [
    { pct: "50%", hold: "3-year hold", active: exitYear === 3 },
    { pct: "75%", hold: "4-year hold", active: exitYear === 4 },
    { pct: "100%", hold: "5-year+ hold", active: exitYear >= 5 },
  ];

  const lenderMult = Number(B.lenderMult) || 0;
  const derived = !!(lev && lev.addon && lev.addon.ebitda > 0 && lev.all && lev.all.ebitda > 0);
  const levCols = (derived
    ? [{ key: "addon", label: lev.addon.label || `Average add-on${lev.addon.count ? ` (of ${lev.addon.count})` : ""}`, ebitda: lev.addon.ebitda, mult: lev.addon.mult },
       { key: "all", label: lev.all.label || "Platform + all add-ons", ebitda: lev.all.ebitda, mult: lev.all.mult }]
    : [{ key: "A", label: "Deal A", ebitda: Number(B.levAEbitda) || 0, mult: Number(B.levAMult) || 0 },
       { key: "B", label: "Deal B", ebitda: Number(B.levBEbitda) || 0, mult: Number(B.levBMult) || 0 }]
  ).map((c) => {
    const price = c.ebitda * c.mult;
    const lenderLimit = c.ebitda * lenderMult;
    const relLev = price ? lenderLimit / price : 0;
    const equity = Math.max(0, price - lenderLimit);
    return { ...c, price, lenderLimit, relLev, equity };
  });

  return (
    <>
    <Card title="QSBS Is the Tax Engine" testid="ru-qsbs"
      right={<span style={{ fontSize: 10.5, color: "var(--text3)", fontStyle: "italic" }}>Section 1202 · this deal's LP outcome</span>}>
      <div className="def-box" data-testid="ru-qsbs-def">
        <div className="def-hd">What is QSBS?</div>
        <div className="def-bd">
          <b>Qualified Small Business Stock</b> — under IRC <b>Section 1202</b>, founders and early investors who hold
          stock in a qualifying domestic C-corporation for at least five years can exclude up to <b>100%</b> of the
          federal capital-gains tax when they sell, capped at the greater of $15M or 10× their basis.
        </div>
      </div>
      <div className="qsbs-tiers" data-testid="ru-qsbs-tiers">
        {tiers.map((t) => (
          <div key={t.pct} className={`qsbs-tier ${t.active ? "active" : ""}`}>
            <div className="qp">{t.pct}</div>
            <div className="ql">of the gain excluded</div>
            <div className="qh">{t.hold}</div>
          </div>
        ))}
      </div>
      <div className="econ-lands" style={{ marginTop: 12 }}>
        <div className="econ-note"><b>How much:</b> up to the greater of $15M or 10× your basis, per company — here {fmtM(cap)}.</div>
        <div className="econ-note" style={{ marginTop: 4 }}><b>What qualifies:</b> a domestic C-corp with $75M or less of gross assets when the stock is issued.</div>
      </div>
      <div className="section-label" style={{ marginTop: 14 }}>What it's worth — this deal's base case ({fmtM(equity)} LP equity → {fmtM(toInvestors)} distributed)</div>
      <table className="tbl" data-testid="ru-qsbs-table" style={{ marginTop: 6 }}>
        <thead><tr><th></th><th className="num">Without QSBS</th><th className="num">With QSBS</th></tr></thead>
        <tbody>
          <tr><td>Gain on the equity</td><td className="num">{fmtM(gain)}</td><td className="num">{fmtM(gain)}</td></tr>
          <tr><td>Federal tax at {(rate * 100).toFixed(1)}%</td><td className="num exp">({fmtM(taxWithout)})</td><td className="num">({fmtM(taxWith)})</td></tr>
          <tr className="total"><td>Net to investors</td><td className="num">{fmtM(netWithout)}</td><td className="num" style={{ color: "var(--lime)" }}>{fmtM(netWith)}</td></tr>
          <tr><td>After-tax multiple on invested capital</td><td className="num">{fmtX(multWithout)}</td><td className="num" style={{ color: "var(--lime)" }}>{fmtX(multWith)}</td></tr>
        </tbody>
      </table>
      <div className="econ-note" style={{ marginTop: 8, fontStyle: "italic", color: "var(--text3)" }}>
        Assumes a {exitYear}-year hold ({exclPct * 100}% exclusion) and the gain within the cap — worth about {fmtM(taxWithout - taxWith)} to investors.
      </div>
    </Card>

    <Card title="Absolute vs. Relative Leverage" testid="ru-leverage"
      right={<span style={{ fontSize: 10.5, color: "var(--text3)", fontStyle: "italic" }}>{derived ? "this deal · average add-on vs. consolidated" : "illustrative"}</span>}>
      <div className="bullet" style={{ marginBottom: 12 }}>
        The same debt can look conservative to a lender and aggressive to a buyer. Which measure you use changes how
        deal size drives your equity check.
      </div>
      <div className="econ-cards" style={{ gridTemplateColumns: "1fr 1fr" }}>
        <div className="econ-card">
          <div className="econ-hd">Absolute leverage</div>
          <div className="econ-note">Debt as a multiple of cash flow — {fmtX(lenderMult)} EBITDA, say. This is what the lender underwrites: can the business service the loan?</div>
        </div>
        <div className="econ-card">
          <div className="econ-hd">Relative leverage</div>
          <div className="econ-note">Debt as a percentage of purchase price. This is what the buyer feels — it sets your equity check, and therefore your return on equity.</div>
        </div>
      </div>

      {!readOnly && <div className="econ-controls" style={{ gridTemplateColumns: derived ? "repeat(1, minmax(160px, 260px))" : "repeat(5, 1fr)", marginTop: 14 }} data-testid="ru-lev-controls">
        <Num label="Lender's limit" value={B.lenderMult} onChange={(v) => set("lenderMult", v)} suffix="x" testid="lev-lender" />
        {!derived && <><Num label="Deal A — EBITDA" value={B.levAEbitda} onChange={(v) => set("levAEbitda", v)} suffix="$" testid="lev-a-ebitda" />
        <Num label="Deal A — multiple" value={B.levAMult} onChange={(v) => set("levAMult", v)} suffix="x" testid="lev-a-mult" />
        <Num label="Deal B — EBITDA" value={B.levBEbitda} onChange={(v) => set("levBEbitda", v)} suffix="$" testid="lev-b-ebitda" />
        <Num label="Deal B — multiple" value={B.levBMult} onChange={(v) => set("levBMult", v)} suffix="x" testid="lev-b-mult" /></>}
      </div>}

      <table className="tbl" data-testid="ru-lev-table" style={{ marginTop: 6 }}>
        <thead><tr>
          <th></th>
          <th className="num">{levCols[0].label}<div className="lev-sub">{fmtM(levCols[0].ebitda)} EBITDA at {fmtX(levCols[0].mult)}</div></th>
          <th className="num">{levCols[1].label}<div className="lev-sub">{fmtM(levCols[1].ebitda)} EBITDA at {fmtX(levCols[1].mult)}</div></th>
        </tr></thead>
        <tbody>
          <tr><td>Purchase price</td><td className="num">{fmtM(levCols[0].price)}</td><td className="num">{fmtM(levCols[1].price)}</td></tr>
          <tr><td>Lender's limit at {fmtX(lenderMult)} EBITDA</td><td className="num">{fmtM(levCols[0].lenderLimit)}</td><td className="num">{fmtM(levCols[1].lenderLimit)}</td></tr>
          <tr className="total"><td>Relative leverage</td><td className="num" style={{ color: "var(--lime)" }}>{Math.round(levCols[0].relLev * 100)}%</td><td className="num" style={{ color: "var(--lime)" }}>{Math.round(levCols[1].relLev * 100)}%</td></tr>
          <tr className="total"><td>Equity required</td><td className="num">{fmtM(levCols[0].equity)}</td><td className="num">{fmtM(levCols[1].equity)}</td></tr>
        </tbody>
      </table>
      <div className="econ-note" style={{ marginTop: 8 }}>
        {derived
          ? <>Same lender, same absolute leverage — one average add-on versus the whole platform. Buying smaller, cheaper
            add-ons turns the same debt appetite into more relative leverage; consolidated, the platform's blended multiple
            pulls it back. High relative leverage means the underwriting has to be right — less equity cushion per deal.</>
          : <>Same lender, same absolute leverage — but buying smaller, cheaper assets turns the same debt appetite into far
            more relative leverage. The flip side: high relative leverage means the underwriting has to be right — there's
            less equity cushion per deal.</>}
      </div>
    </Card>
    </>
  );
}
